import React, { memo, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Input, Dropdown, Loading } from '../../../../../common/ui';
import { updateUser, getUserById, findByUsername, resetSearchedUser } from '../../actions';
import { showAlert } from '../../../../../common/actions';
import styles from './styles.module.css';

const UpdateUser = memo(({
  updateUser,
  getUserById,
  findByUsername,
  resetSearchedUser,
  showAlert,
  auth: { companyId, branchId, branchList },
  usersState: { searchedUserById },
}) => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [originalUsername, setOriginalUsername] = useState('');

  const [isErrorRole, setIsErrorRole] = useState(false);
  const [isErrorBranch, setIsErrorBranch] = useState(false);
  const [isErrorName, setIsErrorName] = useState(false);
  const [isErrorAge, setIsErrorAge] = useState(false);
  const [isErrorGender, setIsErrorGender] = useState(false);
  const [isErrorAddress, setIsErrorAddress] = useState(false);
  const [isErrorEmail, setIsErrorEmail] = useState(false);
  const [isErrorPhone, setIsErrorPhone] = useState(false);
  const [isErrorUserName, setIsErrorUserName] = useState(false);
  const [isErrorPin, setIsErrorPin] = useState(false);

  const [roleErrorMessage, setRoleErrorMessage] = useState('');
  const [branchErrorMessage, setBranchErrorMessage] = useState('');
  const [nameErrorMessage, setNameErrorMessage] = useState('');
  const [ageErrorMessage, setAgeErrorMessage] = useState('');
  const [genderErrorMessage, setGenderErrorMessage] = useState('');
  const [addressErrorMessage, setAddressErrorMessage] = useState('');
  const [emailErrorMessage, setEmailErrorMessage] = useState('');
  const [phoneErrorMessage, setPhoneErrorMessage] = useState('');
  const [userNameErrorMessage, setUserNameErrorMessage] = useState('');
  const [pinErrorMessage, setPinErrorMessage] = useState('');

  const [userData, setUserData] = useState({
    id: '',
    name: '',
    age: '',
    gender: '',
    email: '',
    phone: '',
    address: '',
    username: '',
    password: '',
    pin: '',
    role: '',
    branchId: '',
  });

  const roleOptions = [
    { value: 'admin', label: 'Admin' },
    { value: 'cashier', label: 'Cashier' },
    { value: 'branchManager', label: 'Branch Manager' },
  ];

  const genderOptions = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
  ];

  useEffect(() => {
    resetSearchedUser();
    if (id) {
      getUserById(id);
    }
  }, [id, getUserById, resetSearchedUser]);

  useEffect(() => {
    if (searchedUserById) {
      setUserData({
        id: searchedUserById.id || '',
        name: searchedUserById.name || '',
        age: searchedUserById.age || '',
        gender: searchedUserById.gender || '',
        email: searchedUserById.email || '',
        phone: searchedUserById.phone || '',
        address: searchedUserById.address || '',
        username: searchedUserById.username || '',
        password: '',
        pin: '',
        role: searchedUserById.role || '',
        branchId: branchId || '',
      });
      setOriginalUsername(searchedUserById.username || '');
      setIsLoading(false);
    }
  }, [searchedUserById, branchId]);

  const validations = async () => {
    let isValid = true;

    if (!userData.branchId && userData.role !== 'admin') {
      isValid = false;
      setIsErrorBranch(true);
      setBranchErrorMessage('Branch is required');
    }

    if (!userData.name) {
      isValid = false;
      setIsErrorName(true);
      setNameErrorMessage('Name is required');
    }

    if (!userData.gender) {
      isValid = false;
      setIsErrorGender(true);
      setGenderErrorMessage('Gender is required');
    }

    if (!userData.phone) {
      isValid = false;
      setIsErrorPhone(true);
      setPhoneErrorMessage('Phone number is required');
    }

    if (!userData.username) {
      isValid = false;
      setIsErrorUserName(true);
      setUserNameErrorMessage('Username is required');
    } else {
      if (userData.username !== originalUsername) {
        const isUsernameExists = await findByUsername(userData.username);
        if (isUsernameExists) {
          isValid = false;
          setIsErrorUserName(true);
          setUserNameErrorMessage('Username already exists');
        } else {
          setIsErrorUserName(false);
          setUserNameErrorMessage('');
        }
      } else {
        setIsErrorUserName(false);
        setUserNameErrorMessage('');
      }
    }

    if (userData.pin && !/^\d{6}$/.test(userData.pin)) {
      isValid = false;
      setIsErrorPin(true);
      setPinErrorMessage('PIN must be a 6-digit number');
    }

    return isValid;
  };

  const handleChange = (e) => {
    const { id: fieldId, value } = e.target;

    if (fieldId === 'name') {
      setIsErrorName(false);
      setNameErrorMessage('');
    }
    if (fieldId === 'age') {
      if (!/^\d{0,2}$/.test(value)) {
        setIsErrorAge(true);
        setAgeErrorMessage('Age must be a number with max 2 digits');
        return;
      }
      setIsErrorAge(false);
      setAgeErrorMessage('');
    }
    if (fieldId === 'address') {
      setIsErrorAddress(false);
      setAddressErrorMessage('');
    }
    if (fieldId === 'email') {
      if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        setIsErrorEmail(true);
        setEmailErrorMessage('Please enter a valid email address');
      } else {
        setIsErrorEmail(false);
        setEmailErrorMessage('');
      }
    }
    if (fieldId === 'phone') {
      if (!/^\d*$/.test(value)) {
        setIsErrorPhone(true);
        setPhoneErrorMessage('Phone number must contain only numbers');
        return;
      }
      setIsErrorPhone(false);
      setPhoneErrorMessage('');
    }
    if (fieldId === 'username') {
      setIsErrorUserName(false);
      setUserNameErrorMessage('');
    }
    if (fieldId === 'pin') {
      if (!/^\d{0,6}$/.test(value)) {
        setIsErrorPin(true);
        setPinErrorMessage('PIN must be a 6-digit number');
        return;
      }
      setIsErrorPin(false);
      setPinErrorMessage('');
    }

    setUserData((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    if (name === 'gender') {
      setIsErrorGender(false);
      setGenderErrorMessage('');
    }
    setUserData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    const isValid = await validations();

    if (isValid) {
      setIsSubmitting(true);
      const user = {
        id: userData.id,
        name: userData.name,
        age: userData.age || null,
        gender: userData.gender,
        address: userData.address,
        email: userData.email,
        phone: userData.phone,
        username: userData.username,
        role: userData.role,
        ...(userData.password ? { password: userData.password } : {}),
        ...(userData.pin ? { pin: userData.pin } : {}),
      };

      const requestBody = { user };
      const isSuccess = await updateUser(requestBody);

      if (isSuccess) {
        showAlert({
          isAlertOpen: true,
          alertType: 'success',
          alertMessage: 'User updated successfully',
        });
        navigate('/iam/users');
      } else {
        showAlert({
          isAlertOpen: true,
          alertType: 'danger',
          alertMessage: 'Something went wrong. Please Try Again.',
        });
      }
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    if (searchedUserById) {
      setUserData({
        id: searchedUserById.id || '',
        name: searchedUserById.name || '',
        age: searchedUserById.age || '',
        gender: searchedUserById.gender || '',
        email: searchedUserById.email || '',
        phone: searchedUserById.phone || '',
        address: searchedUserById.address || '',
        username: searchedUserById.username || '',
        password: '',
        pin: '',
        role: searchedUserById.role || '',
        branchId: branchId || '',
      });
    }
    setIsErrorRole(false);
    setIsErrorBranch(false);
    setIsErrorName(false);
    setIsErrorAge(false);
    setIsErrorGender(false);
    setIsErrorAddress(false);
    setIsErrorEmail(false);
    setIsErrorPhone(false);
    setIsErrorUserName(false);
    setIsErrorPin(false);
    setRoleErrorMessage('');
    setBranchErrorMessage('');
    setNameErrorMessage('');
    setAgeErrorMessage('');
    setGenderErrorMessage('');
    setAddressErrorMessage('');
    setEmailErrorMessage('');
    setPhoneErrorMessage('');
    setUserNameErrorMessage('');
    setPinErrorMessage('');
  };

  const handleCancel = () => {
    navigate('/iam/users');
  };

  const branchOptions = (branchList || []).map((branch) => ({
    value: branch.id,
    label: branch.name || branch.branchName,
  }));

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.pageTitle}>Update User</h1>
          <p className={styles.caption}>Update the User in your store</p>
        </div>
        <div className={styles.formCard}>
          <div className={styles.loadingWrap}>
            <Loading variant="overlay" label="Loading user…" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.pageTitle}>Update User</h1>
        <p className={styles.caption}>Update the User in your store</p>
      </div>
      <div className={styles.formCard}>
        <div className={styles.roleSection}>
          <div className={styles.selectWrap}>
            <Dropdown
              id="role"
              label="User Role"
              options={roleOptions}
              value={userData.role}
              placeholder="Select Role"
              onChange={(value) => handleSelectChange({ target: { name: 'role', value } })}
              disabled
              triggerClassName={isErrorRole ? styles.triggerError : undefined}
            />
            {isErrorRole && (
              <span className={styles.fieldError}>{roleErrorMessage}</span>
            )}
          </div>
          <div className={styles.selectWrap}>
            <Dropdown
              id="branchId"
              label="Select Branch"
              options={branchOptions}
              value={userData.branchId}
              placeholder="Select Branch"
              onChange={(value) => handleSelectChange({ target: { name: 'branchId', value } })}
              disabled
              triggerClassName={isErrorBranch ? styles.triggerError : undefined}
            />
            {isErrorBranch && (
              <span className={styles.fieldError}>{branchErrorMessage}</span>
            )}
          </div>
        </div>
        <div className={styles.divider} />
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>User Profile</h2>
          <div className={styles.fieldRow}>
            <div className={styles.fieldWrap}>
              <Input
                id="name"
                label="Name"
                value={userData.name}
                onChange={(value) => handleChange({ target: { id: 'name', value } })}
                error={isErrorName ? nameErrorMessage : undefined}
              />
            </div>
            <div className={styles.fieldWrap}>
              <Input
                id="age"
                label="Age"
                value={userData.age}
                onChange={(value) => handleChange({ target: { id: 'age', value } })}
                error={isErrorAge ? ageErrorMessage : undefined}
              />
            </div>
          </div>
          <div className={styles.fieldRow}>
            <div className={styles.selectWrap}>
              <Dropdown
                id="gender"
                label="Gender"
                options={genderOptions}
                value={userData.gender}
                placeholder="Select Gender"
                onChange={(value) => handleSelectChange({ target: { name: 'gender', value } })}
                triggerClassName={isErrorGender ? styles.triggerError : undefined}
              />
              {isErrorGender && (
                <span className={styles.fieldError}>{genderErrorMessage}</span>
              )}
            </div>
            <div className={styles.fieldWrap}>
              <Input
                id="address"
                label="Address"
                value={userData.address}
                onChange={(value) => handleChange({ target: { id: 'address', value } })}
                error={isErrorAddress ? addressErrorMessage : undefined}
              />
            </div>
          </div>
          <div className={styles.fieldRow}>
            <div className={styles.fieldWrap}>
              <Input
                id="email"
                label="Email"
                type="email"
                value={userData.email}
                onChange={(value) => handleChange({ target: { id: 'email', value } })}
                error={isErrorEmail ? emailErrorMessage : undefined}
              />
            </div>
            <div className={styles.fieldWrap}>
              <Input
                id="phone"
                label="Phone"
                value={userData.phone}
                onChange={(value) => handleChange({ target: { id: 'phone', value } })}
                error={isErrorPhone ? phoneErrorMessage : undefined}
              />
            </div>
          </div>
        </div>
        <div className={styles.divider} />
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>User Credentials</h2>
          <div className={styles.fieldRow}>
            <div className={styles.fieldWrap}>
              <Input
                id="username"
                label="Login User Name"
                value={userData.username}
                onChange={(value) => handleChange({ target: { id: 'username', value } })}
                error={isErrorUserName ? userNameErrorMessage : undefined}
              />
            </div>
            <div className={styles.fieldWrap}>
              <Input
                id="password"
                label="Update Password (leave blank to keep current)"
                type="password"
                value={userData.password}
                onChange={(value) => handleChange({ target: { id: 'password', value } })}
              />
            </div>
          </div>
          <div className={styles.fieldRow}>
            <div className={styles.fieldWrap}>
              <Input
                id="pin"
                label="PIN (leave blank to keep current)"
                type="password"
                value={userData.pin}
                onChange={(value) => handleChange({ target: { id: 'pin', value } })}
                error={isErrorPin ? pinErrorMessage : undefined}
                placeholder="6-digit PIN"
              />
            </div>
          </div>
          <div className={styles.actionsRow}>
            <Button
              type="button"
              className={styles.actionBtn}
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Loading...' : 'Update'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              className={styles.actionBtn}
              onClick={handleReset}
            >
              Reset
            </Button>
            <Button
              type="button"
              variant="secondary"
              className={styles.actionBtn}
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});

const mapStateToProps = (state) => ({
  auth: state.authState,
  usersState: state.usersState,
});

export default connect(mapStateToProps, {
  updateUser,
  getUserById,
  findByUsername,
  resetSearchedUser,
  showAlert,
})(UpdateUser);
